import { ChevronDownIcon } from '@heroicons/react/24/outline';
import React from 'react';

import { cn } from '@/utils/classname';

interface InfiniteScrollIndicatorProps {
  /**
   * Whether data is currently being fetched for the next page.
   * Typically mapped to `isFetchingNextPage` from TanStack Query.
   */
  isLoading: boolean;

  /**
   * Whether there is more data available to load.
   * Typically mapped to `hasNextPage === true` from TanStack Query.
   */
  hasMore: boolean;

  /**
   * Total number of items currently loaded in the table.
   * Used to determine if indicator should be shown based on threshold.
   */
  itemCount: number;

  /**
   * Minimum number of items required before showing the indicator.
   * Defaults to 10 items.
   */
  minItemsThreshold?: number;

  /**
   * Positioning variant for the indicator.
   * - 'fixed': Fixed at viewport bottom with backdrop blur and border (default)
   * - 'relative': Relative positioning for modal contexts, simpler styling
   * - 'sticky': Sticky positioning for scroll containers, same styling as fixed
   * @default 'fixed'
   */
  variant?: 'fixed' | 'relative' | 'sticky';
}

/**
 * InfiniteScrollIndicator - Unified indicator for infinite scroll state
 *
 * Displays a sticky indicator at the bottom of scrollable tables that transitions
 * between two states:
 * - Idle: "Scroll for more entries" with chevron icon
 * - Loading: "Loading more data..." with spinner
 *
 * Design Requirements:
 * - Single component that replaces separate scroll hint + loading indicator
 * - Positioned inside scrollable container for proper sticky behavior
 * - Instant state transitions (no fade/slide animations)
 * - Semi-transparent background with backdrop blur
 * - Consistent position and styling across both states
 * - z-10 to avoid conflicts with modal overlays (z-50+)
 *
 * Visibility Logic:
 * - Shows if hasMore OR isLoading (prevents disappearing mid-load)
 * - Only displays when itemCount >= minItemsThreshold (default 10)
 * - Remains visible during loading even if hasMore becomes false
 *
 * Accessibility:
 * - Dynamic ARIA role: "status" during loading, "complementary" when idle
 * - Dynamic aria-live: "polite" during loading, "off" when idle
 * - aria-busy attribute reflects loading state
 * - aria-atomic="true" ensures full message is announced
 * - aria-label provides contextual information with item count for screen readers
 *
 * @example
 * ```tsx
 * <div className="overflow-auto">
 *   <table>
 *     {/* table content *\/}
 *   </table>
 *   <InfiniteScrollIndicator
 *     isLoading={isFetchingNextPage}
 *     hasMore={hasNextPage === true}
 *     itemCount={data.length}
 *     minItemsThreshold={10}
 *   />
 * </div>
 * ```
 */
export const InfiniteScrollIndicator: React.FC<
  InfiniteScrollIndicatorProps
> = ({
  isLoading,
  hasMore,
  itemCount,
  minItemsThreshold = 10,
  variant = 'fixed',
}) => {
  // Visibility: Show if either hasMore OR isLoading (prevents disappearing mid-load)
  // AND itemCount meets minimum threshold
  const isVisible = (hasMore || isLoading) && itemCount >= minItemsThreshold;

  if (!isVisible) return null;

  // Determine which styling to apply based on variant
  const hasFullStyling = variant === 'fixed' || variant === 'sticky';

  return (
    <div
      className={cn(
        // Flexbox centering with consistent gap
        'flex items-center justify-center gap-2',
        // Typography: small, medium weight, secondary text color
        'text-chariot-text-secondary text-sm font-medium',
        // Variant-specific positioning
        variant === 'fixed' && 'fixed right-0 bottom-0 left-0 z-10',
        variant === 'sticky' && 'sticky right-0 bottom-0 left-0',
        variant === 'relative' && 'shrink-0',
        // Variant-specific styling
        hasFullStyling
          ? [
              // Padding for touch targets and visual spacing
              'px-4 py-2',
              // Semi-transparent background with backdrop blur for readability
              'bg-chariot-background-secondary backdrop-blur-xs',
              // Top border using theme stroke color
              'border-chariot-stroke border-t',
            ]
          : [
              // Simpler padding for modal context
              'py-2',
            ]
      )}
      // Dynamic ARIA role based on loading state
      role={isLoading ? 'status' : 'complementary'}
      // Dynamic aria-live: announce loading, silent when idle
      aria-live={isLoading ? 'polite' : 'off'}
      // Busy state reflects loading
      aria-busy={isLoading}
      // Atomic announcement for complete message
      aria-atomic="true"
      // Contextual label for screen readers with item count
      aria-label={
        isLoading
          ? `Loading additional table entries. ${itemCount} items currently loaded.`
          : `${itemCount} items loaded. Scroll down to load more entries.`
      }
      // Test identifier for E2E tests
      data-testid="infinite-scroll-indicator"
    >
      {isLoading ? (
        <>
          {/* Spinner SVG - inline to match design requirements */}
          <svg
            className="size-4 animate-spin"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
          <span>Loading more data...</span>
        </>
      ) : (
        <>
          {/* Chevron icon for scroll hint */}
          <ChevronDownIcon className="size-4" aria-hidden="true" />
          <span>Scroll for more entries</span>
        </>
      )}
    </div>
  );
};
